import { utils } from '@hh/global.js'
const { serializeForm } = utils

/**
 * Convert rgb to hex
 *
 * @param {r, g, b} int rgb values
 */
function componentToHex(c) {
  const hex = c.toString(16)
  return hex.length == 1 ? '0' + hex : hex
}

const rgbToHex = (r, g, b) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

class ProductVariants extends HHBaseElement {
  productHandle = null
  productHTML = ''

  static get observedAttributes() {
    return ['variantId']
  }

  constructor() {
    super()

    this.mainProduct =
      this.closest('mini-product-card') ||
      this.closest('product-card') ||
      this.closest('main-product')

    this.allSwatches = [
      ...this.querySelectorAll(
        '.swatch .js-swatch-radio:not(.swatch-count-container)'
      ),
    ]
    this.allSizeButtons = [
      ...this.querySelectorAll('.size-variants-container button'),
    ]
    // variants that aren't size or color
    this.allVariantDropdownOptions = [
      ...this.mainProduct.querySelectorAll(
        '.general-variants-container variant-dropdown .dropdown-options input'
      ),
    ]

    this.productForm = this.mainProduct.querySelector('form')
    this.atcButton = this.mainProduct.querySelector('#add-to-cart')
    this.quickShopParent = this.closest('quick-shop')

    this._updateVariant = this._updateVariant.bind(this)
    this._initOptionSelectorMap = this._initOptionSelectorMap.bind(this)
    this.onSubmitHandler = this.onSubmitHandler.bind(this)
    this._selectQuizVariant = this._selectQuizVariant.bind(this)
  }

  async connectedCallback() {
    this._isReadyPromise = new Deferred()
    await this.mainProduct.isReadyPromise

    this.addToCartButton = this.mainProduct.querySelector('[type="submit"]')
    this.variantInput = this.mainProduct.querySelector('[name="id"]')

    this._initOptionSelectorMap()
    this._attachListeners()
    this._randomizeInputIds()
    this._selectQuizVariant()
    this._isReadyPromise.resolve()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  get isReadyPromise() {
    return this._isReadyPromise.promise
  }

  get isUpdatingLineItem() {
    return this.quickShopParent && !!this.quickShopParent.editLineItemKey
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get variantType() {
    return this.getAttribute('variantType')?.trim()
  }

  set variantId(id) {
    this.setAttribute('variantId', id)
  }

  get variantId() {
    return this.getAttribute('variantId')
  }

  get featuredVariantId() {
    return this.getAttribute('featuredVariantId')
  }

  get product() {
    return this.mainProduct?.product
  }

  _attachListeners() {
    if (this.variantType === 'color') {
      this.querySelectorAll('.js-swatch-radio').forEach((radio) => {
        radio.addEventListener('change', this._updateVariant.bind(this))
      })

      let matchingButton = this.allSwatches.filter(
        (button) => button.dataset.variantId === this.variantId
      )?.[0]

      // matchingButton?.click()
    } else if (this.variantType === 'size') {
      this.allSizeButtons.forEach((button) =>
        button.addEventListener('click', this._updateVariant.bind(this))
      )
    } else if (
      this.variantType === 'other' ||
      this.variantType === 'general-variants'
    ) {
      this.allVariantDropdownOptions.forEach((input) =>
        input.addEventListener('change', this._updateVariant.bind(this))
      )
    }

    this.addToCartButton?.addEventListener('click', this.onSubmitHandler)
  }

  _removeListeners() {
    if (this.variantType === 'color') {
      this.allSwatches.forEach((swatch) =>
        swatch.removeEventListener('click', this._updateVariant)
      )
    } else if (this.variantType === 'size') {
      this.allSizeButtons.forEach((button) =>
        button.removeEventListener('click', this._updateVariant)
      )
    }

    this.addToCartButton?.removeEventListener('click', this.onSubmitHandler)
  }

  _randomizeInputIds() {
    // Append random integer in order to prevent duplicate element IDs when multiple of the same products are rendered on the page
    const radios = this.querySelectorAll('.js-swatch-radio')
    radios.forEach((radio) => {
      const newId = `${radio.id}-${Math.floor(Math.random() * 10000000)}`
      radio.id = newId
      radio.closest('label').setAttribute('for', newId)
    })
  }

  _updateVariant(e) {
    let newVariantValue = e.target.dataset.label
      ? e.target.dataset.label
      : e.target.value
    let newVariantId = e.target.dataset.variantId

    let isSelector = this.querySelector('.single-option-selector')
    let isSizeSelector = this.querySelector('.size-variants-container')
    let isGeneralVariants = this.variantType === 'general-variants'

    this.variantId = newVariantId

    if (isSelector && !isGeneralVariants) {
      this.variantSelector = this.querySelector('.single-option-selector')
      let backgroundColor = e.target.style.backgroundColor
      if (backgroundColor.indexOf('rgb') > -1) {
        backgroundColor = backgroundColor
          .replace('rgb(', '')
          .replace(')')
          .split(', ')
        const r = parseInt(backgroundColor[0]) || 0
        const g = parseInt(backgroundColor[1]) || 0
        const b = parseInt(backgroundColor[2].replace('undefined', '')) || 0
        backgroundColor = rgbToHex(r, g, b)
      }

      if (newVariantValue && isSelector) {
        if (
          !isSizeSelector &&
          this.mainProduct.querySelector('.js-selected-variant-hex')
        ) {
          this.mainProduct.querySelector('.js-selected-variant-hex').value =
            backgroundColor
        }

        let variantMapValue = Array.from(this.variantMap).filter(
          (variant) => variant.name === newVariantValue
        )?.[0]?.optionGenName

        this.variantSelector.value = variantMapValue
        let event = new Event('change')
        this.variantSelector.dispatchEvent(event)
      }
    } else {
      let div = this
      let variantMap = this.variantMap

      setTimeout(function () {
        if (div.querySelector('variant-dropdown')) {
          div
            .querySelector('variant-dropdown')
            .setAttribute('variant', newVariantId)
        }

        let variantMapValue = variantMap.filter(
          (variant) => variant.name === newVariantValue
        )?.[0]?.optionGenName

        if (isSelector) {
          isSelector.value = variantMapValue
          isSelector.dispatchEvent(new Event('change'))
        }
      }, 20)
    }

    this._updateProductCardUrls(newVariantId)
  }

  _updateProductCardUrls(newVariantId) {
    const productCard = this.closest('product-card')
    if (!productCard) {
      return
    }

    const productCardLinks = productCard.querySelectorAll(
      '.js-product-card-link'
    )
    productCardLinks.forEach((productCardLink) => {
      const url = new URL(
        productCardLink.getAttribute('href'),
        window.location.origin
      )
      const params = new URLSearchParams(url.search)

      params.set('variant', newVariantId)

      url.search = params
      productCardLink.setAttribute('href', url.href)
    })
  }

  // This creates a map for easier variant switching
  _initOptionSelectorMap() {
    if (this.variantType === 'none' || !this.variantType) {
      return
    }

    let liquidVariants = this.querySelector('.hidden-variants')
    let selector =
      this.querySelector('.single-option-selector') ||
      this.querySelector('select') ||
      liquidVariants
    let options = [...selector.querySelectorAll('option')]

    let liquidOptions = [...liquidVariants.querySelectorAll('option')]

    let rawVariants = this.product.variants

    this.variantMap = options.map((option) => {
      return {
        value: rawVariants.find((v) => v.title === option.innerText)?.id,
        name: liquidOptions.find((o) => o.innerText === option.innerText)
          ?.innerText,
        optionGenName: option.value,
      }
    })
  }

  nostoAttribution() {
    const nostoElement = this.closest('.nosto_element')

    // When ATC is submitted from the swatch selector slideout, the slideout is
    // not nested inside a .nosto_element. We store the placement ID on it when
    // it's opened from a Nosto card, so check there as a fallback.
    const slideout = document.querySelector(
      '.slideout-swatch-selector[data-nosto-placement-id]'
    )
    const placementId = nostoElement?.id || slideout?.dataset.nostoPlacementId

    if (!placementId) return

    this.triggerEvent('click::add-to-cart', {
      product_id: this.productId,
      placement_id: placementId,
    })

    // Clear after use so a subsequent non-Nosto cart add doesn't get attributed.
    if (slideout) delete slideout.dataset.nostoPlacementId
  }

  fillNotifyMeModal(params) {
    const notifyMeImage = document.querySelector('.js-slideout-notify-me-image')
    const notifyMeTitle = document.querySelector('.js-slideout-notify-me-title')
    const notifyMeVariant = document.querySelector(
      '.js-slideout-notify-me-variant'
    )
    const notifyMeSku = document.querySelector('#slideout-notify-me-sku')
    const notifyMeProductId = document.querySelector(
      '#slideout-notify-me-product-id'
    )
    const notifyMeVariantId = document.querySelector(
      '#slideout-notify-me-variant-id'
    )
    if (notifyMeVariantId) notifyMeVariantId.value = params.variantId

    notifyMeImage.innerHTML = `<img src="${params.image}" alt="${params.productTitle}" />`
    notifyMeTitle.innerHTML = params.productTitle
    if (params.title.toLowerCase() === 'default title') {
      notifyMeVariant.innerHTML = ''
    } else {
      notifyMeVariant.innerHTML = `${
        params.hexCode
          ? `<span class="block" style="background-color: ${params.hexCode};"></span>`
          : ''
      }${params.title}`
    }
    notifyMeSku.value = params.sku
    notifyMeProductId.value = params.productId
  }

  async onSubmitHandler(e) {
    e.preventDefault()
    const triggerButton = e.currentTarget

    let selectedVariantId = this.variantInput.value
    const hexcode = document.querySelector('.js-selected-variant-hex')
    const isNotifyMe =
      triggerButton.classList.contains(atcStatusTitle.notifyMeClass[0]) === true

    if (isNotifyMe) {
      let selectedItem = null
      this.product.variants.forEach((item) => {
        if (Number(selectedVariantId) === item.id) {
          selectedItem = item
        }
      })
      const params = {
        productId: this.product.id,
        variantId: selectedVariantId,
        productTitle: selectedItem?.name,
        sku: selectedItem?.sku,
        title: selectedItem?.title,
        image: selectedItem?.featured_image
          ? selectedItem?.featured_image.src
          : this.product.featured_image,
      }
      this.fillNotifyMeModal(params)
    } else {
      if (triggerButton) {
        triggerButton.setAttribute('loading', true)
      }

      const variantID = this.variantInput.value
      const quantity = 1

      this.mainProduct.classList.add('is-loading')

      await window.__CART__CONTROLLER__.add({
        payload: {
          id: variantID,
          quantity: quantity,
        },
      })

      this.mainProduct.classList.remove('is-loading')
      this.nostoAttribution()
    }
  }

  _selectQuizVariant() {
    if (this.closest('quiz-results')) {
      const variantId = this.closest('product-card').getAttribute('data-id')

      let isSwatchFound = false
      this.allSwatches.forEach((swatchRadio) => {
        if (isSwatchFound) return

        const swatchVariantId = swatchRadio.getAttribute('data-variant-id')
        if (variantId === swatchVariantId) {
          isSwatchFound = true
          const swatchLi = swatchRadio.closest('.swatch-element')
          const swatchButton = swatchLi.querySelector('.swatch-color-button')
          swatchButton?.click()
          swatchLi?.classList.add('swatch-element__featured')
        }
      })
    }
  }
}

window.customElements.define('product-variants', ProductVariants)
